import Button from '@creator/ui/components/Button/ButtonV2';
import Image from '@creator/ui/components/Image/Image';
import RtbLogo from '@src/assets/images/rtb-logo.png';
import Icon from '@src/basic-components/Icon/Icon';
import Link from '@src/basic-components/Link/Link';
import Logo from '@src/basic-components/Logo/Logo';
import { ensureTokenUrl, getDomainConfigValue } from '@src/model/config/helpers';
import { useStoreState } from '@src/model/hooks';
import { storeEqualityFn } from '@src/utils/object-utils/object-utils';
import { getUrlPath } from '@src/utils/url-utils/url-utils';
import type { FC } from 'react';
import { Fragment, lazy, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import TokenLogo from '../Token/TokenLogo/TokenLogo';
import BellNotification from './BellNotification/BellNotification';

const MainMenu = lazy(() => import('./MainMenu/MainMenu'));

export interface HeaderProps {
    className?: string;
    tokenName?: string;
    showSearchButton?: boolean;
    showLoginButton?: boolean;
    hideSearch?: boolean;
}

const Header: FC<HeaderProps> = props => {
    const { showSearchButton = true, showLoginButton = true, tokenName, className } = props;

    const navigate = useNavigate();
    const { t } = useTranslation();

    const isLoggedIn = useStoreState(state => state.user.isLoggedIn);
    const isAuthenticating = useStoreState(state => state.user.isAuthenticating);
    const myUser = useStoreState(state => state.user.myUser, storeEqualityFn);

    const onLoginClick = useCallback(() => {
        navigate(`/bbs/login?next=${getUrlPath()}`);
    }, []);

    const onNotificationClick = useCallback(() => {
        navigate('/bbs/notifications');
    }, []);

    const onStakingClick = useCallback(() => {
        const stakingUrl = 'https://staking.rtb.io';
        window.open(stakingUrl);
    }, []);

    function getLogoLink() {
        return getDomainConfigValue('logoLink') || '/';
    }

    function renderLogo() {
        return (
            <Link externalLinkTarget="_self" to={getLogoLink()} className="shrink-0">
                <Logo className="w-auto h-5 transition" />
            </Link>
        );
    }

    function renderLoginButton() {
        if (isAuthenticating || isLoggedIn || !showLoginButton) return null;
        return (
            <Button size="xs" className="" onClick={onLoginClick}>
                {t('header.loginButton')}
            </Button>
        );
    }

    function renderNotificationButton() {
        if (!myUser) return null;

        return <BellNotification className="flex items-center justify-center text-xl cursor-pointer lg:text-2xl" onClick={onNotificationClick} />;
    }

    function renderStakingButton() {
        if (!getDomainConfigValue('enableStakingButton')) return null;
        return <Icon onClick={onStakingClick} name="cup" className="hidden text-xl cursor-pointer lg:text-2xl lg:block text-gray-50" />;
    }

    function renderSearchIcon() {
        if (!showSearchButton || !tokenName) return;
        return <Icon onClick={() => navigate(`/bbs/${ensureTokenUrl(tokenName, false)}/search`)} name="search" className="text-xl cursor-pointer lg:text-2xl text-gray-50" />;
    }

    function renderTokenLogo() {
        if (!tokenName) return null;
        return <TokenLogo tokenName={tokenName} className="h-5 text-xs cursor-pointer lg:text-sm text-gray-50 dark:text-gray-50" />;
    }

    function renderRtbLogo() {
        return (
            <a data-ssr-skip-clean-child-nodes="true" target="_blank" href="https://roundtable.io/about" className="cursor-pointer shrink-0" rel="noreferrer">
                <Image fit="default" src={RtbLogo} className="h-6 lg:h-7" alt="Powered by Roundtable" />
            </a>
        );
    }

    function renderLogosSection() {
        const tokenLogo = renderTokenLogo();

        return (
            <Fragment>
                {renderLogo()}
                {tokenLogo && <div className="w-[0.5px] bg-gray-50 h-1/2" />}
                {tokenLogo}
            </Fragment>
        );
    }
    return (
        <header className={`h-10 px-4 flex items-center transition sticky top-0 z-20 shadow-xs dark:shadow-gray-800 bg-gray-950 ${className}`}>
            <div className="flex items-center h-full space-x-2 lg:space-x-3">
                <MainMenu className="" tokenName={tokenName} />
                {renderLogosSection()}
            </div>
            <div data-ssr-skip-clean-child-nodes="true" className="flex items-center justify-end flex-1 mx-2 mr-2 space-x-2 lg:space-x-3 min-w-max">
                {renderStakingButton()}
                {renderNotificationButton()}
                {renderSearchIcon()}
                {renderLoginButton()}
            </div>
            {renderRtbLogo()}
        </header>
    );
};

export default memo(Header);
